import React, { useState, useRef, useEffect } from 'react';
import { Checkbox, TextField, Icon } from '@fluentui/react';
import './SearchableDropdown.css';

const SearchableDropdown = ({
    label,
    options,
    selectedKeys = [],
    onChange,
    placeholder = "",
    multiSelect = false,
    errorMessage = "",
    onBlur,
    onValueChange
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchText, setSearchText] = useState('');
    const dropdownRef = useRef(null);

    const filteredOptions = options.filter(option =>
        option.text.toLowerCase().includes(searchText.toLowerCase())
    );

    const selectedItems = options.filter(opt => selectedKeys?.includes(opt.key));
    const displayText = selectedItems.length > 0
        ? selectedItems.map(item => item.text).join(', ')
        : '';

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                // Only trigger onBlur if the dropdown was actually open
                if (isOpen) {
                    setIsOpen(false);
                    setSearchText('');
                    // Trigger onBlur when clicking outside an open dropdown
                    if (onBlur) {
                        onBlur();
                    }
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onBlur, isOpen]);

    const handleOptionClick = (option) => {
        if (multiSelect) {
            const isSelected = selectedKeys?.includes(option.key);
            onChange(null, { ...option, selected: !isSelected });
        } else {
            const newValue = option?.key || "";
            onChange(null, option);
            setIsOpen(false);
            setSearchText('');
            // Call onValueChange with the new value for immediate validation
            if (onValueChange) {
                onValueChange(newValue);
            }
        }
    };

    return (
        <div className="searchable-dropdown" ref={dropdownRef}>
            {label && <label className="searchable-dropdown-label">{label}</label>}

            <div
                className={`searchable-dropdown-field ${errorMessage ? 'has-error' : ''}`}
                onClick={() => setIsOpen(!isOpen)}
            >
                <div className="searchable-dropdown-text">
                    {displayText}
                </div>
                <Icon
                    iconName="ChevronDown"
                    className="searchable-dropdown-icon"
                />
            </div>

            {errorMessage && (
                <div className="searchable-dropdown-error">{errorMessage}</div>
            )}

            {isOpen && (
                <div className="searchable-dropdown-menu">
                    <div className="searchable-dropdown-search">
                        <TextField
                            placeholder={placeholder}
                            value={searchText}
                            onChange={(e, newValue) => setSearchText(newValue || '')}
                            autoFocus
                            iconProps={{ iconName: 'Search' }}
                        />
                    </div>

                    <div className="searchable-dropdown-options">
                        {filteredOptions.length === 0 ? (
                            <div className="searchable-dropdown-no-results">No results found</div>
                        ) : (
                            filteredOptions.map(option => {
                                const isSelected = selectedKeys?.includes(option.key);
                                return (
                                    <div
                                        key={option.key}
                                        className={`searchable-dropdown-option ${isSelected ? 'selected' : ''}`}
                                        onClick={(e) => {
                                            if (!e.target.closest('.ms-Checkbox')) {
                                                handleOptionClick(option);
                                            }
                                        }}
                                    >
                                        {multiSelect && (
                                            <Checkbox
                                                checked={isSelected}
                                                onChange={() => handleOptionClick(option)}
                                                styles={{ root: { marginRight: 8 } }}
                                            />
                                        )}
                                        <span>{option.text}</span>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchableDropdown;
